import { useState } from "react";
import {
  Alert,
  Button,
  DataTable,
  InputText,
  SectionCard,
  Select,
  type ColumnDef,
} from "@/deps/ui-library";
import {
  useAdditionalCharges,
  useRemoveAdditionalCharge,
  useSaveAdditionalCharge,
} from "@/api/queries";
import type { AdditionalCharge, VatCategory } from "@/api/types";
import { useTaxWords } from "@/app/session";
import { Dialog } from "@/components/Dialog";
import { CATEGORY_OPTIONS } from "@/components/LineEditor/LineEditor";
import { formatAmount } from "@/lib/money";

type Editing = Omit<AdditionalCharge, "id"> & { id?: string };

const NEW_CHARGE: Editing = {
  name: "",
  reasonCode: "",
  category: "S",
  rate: "13",
  defaultAmount: "",
};

const isAmount = (value: string) => /^\d+(\.\d{1,2})?$/.test(value.replaceAll(",", "").trim());

/** The charges a company adds to invoices. An invoice keeps its own copy of what it took. */
export function AdditionalChargesCard() {
  const say = useTaxWords();
  const { data: charges } = useAdditionalCharges();
  const remove = useRemoveAdditionalCharge();
  const [editing, setEditing] = useState<Editing | null>(null);

  const columns: Array<ColumnDef<AdditionalCharge>> = [
    { id: "name", header: "Charge", cell: ({ row: { original: charge } }) => charge.name },
    {
      id: "code",
      header: "Reason code",
      meta: { numeric: true },
      cell: ({ row: { original: charge } }) => charge.reasonCode,
    },
    {
      id: "vat",
      header: say("VAT"),
      cell: ({ row: { original: charge } }) =>
        charge.category === "O" ? "O" : `${charge.category} · ${charge.rate ?? "0"}%`,
    },
    {
      id: "default",
      header: "Default",
      meta: { textAlign: "right", numeric: true },
      cell: ({ row: { original: charge } }) =>
        charge.defaultAmount ? formatAmount(charge.defaultAmount) : "Varies",
    },
    {
      id: "action",
      header: "",
      cell: ({ row: { original: charge } }) => (
        <span className="flex justify-end gap-1">
          <Button
            colorScheme="default"
            variant="ghost"
            size="xs"
            aria-label={`Edit ${charge.name}`}
            onClick={() => setEditing(charge)}
          >
            Edit
          </Button>
          <Button
            colorScheme="default"
            variant="ghost"
            size="xs"
            disabled={remove.isPending}
            aria-label={`Remove ${charge.name}`}
            onClick={() => remove.mutate(charge.id)}
          >
            Remove
          </Button>
        </span>
      ),
    },
  ];

  return (
    <SectionCard
      title="Additional charges"
      actions={
        <Button
          colorScheme="default"
          variant="outline"
          size="sm"
          onClick={() => setEditing(NEW_CHARGE)}
        >
          New charge
        </Button>
      }
      flush
    >
      {remove.isError ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={(remove.error as Error).message}
        />
      ) : null}
      <DataTable
        aria-label="Additional charges"
        size="sm"
        enableSorting={false}
        columns={columns}
        data={charges ?? []}
        getRowId={(charge) => charge.id}
        loading={charges === undefined}
        emptyContent="No charges yet. A charge such as freight can still be typed on an invoice."
        pagination={{ mode: "manual", show: false }}
      />
      {editing ? <ChargeDialog start={editing} onClose={() => setEditing(null)} /> : null}
    </SectionCard>
  );
}

function ChargeDialog({ start, onClose }: { start: Editing; onClose: () => void }) {
  const say = useTaxWords();
  const save = useSaveAdditionalCharge();
  const [form, setForm] = useState(start);

  const rateOk = form.category !== "S" || Number(form.rate ?? "0") > 0;
  const amountOk = form.defaultAmount.trim() === "" || isAmount(form.defaultAmount);

  return (
    <Dialog
      open
      onClose={onClose}
      title={start.id ? "Edit charge" : "New charge"}
      footer={
        <Button
          colorScheme="primary"
          variant="solid"
          size="sm"
          disabled={form.name.trim() === "" || !rateOk || !amountOk || save.isPending}
          onClick={() =>
            save.mutate(
              { ...form, defaultAmount: form.defaultAmount.replaceAll(",", "").trim() },
              { onSuccess: onClose },
            )
          }
        >
          {save.isPending ? "Saving" : "Save"}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        {save.isError ? (
          <Alert
            config={{ variant: "subtle", colorScheme: "danger" }}
            description={(save.error as Error).message}
          />
        ) : null}
        <InputText
          label="Charge name"
          required
          placeholder="e.g. Packing and forwarding"
          value={form.name}
          onChange={(event) => setForm({ ...form, name: event.target.value })}
        />
        <InputText
          label="Reason code"
          placeholder="e.g. FC"
          className="num"
          value={form.reasonCode}
          onChange={(event) => setForm({ ...form, reasonCode: event.target.value })}
        />
        <div className="grid gap-3 md:grid-cols-2">
          <Select
            label={say("VAT category")}
            options={CATEGORY_OPTIONS}
            value={form.category}
            showClearButton={false}
            onChange={(details) => {
              const chosen = Array.isArray(details.value) ? details.value[0] : details.value;
              if (!chosen) return;
              const category = String(chosen.value) as VatCategory;
              setForm({ ...form, category, rate: category === "S" ? (form.rate ?? "13") : null });
            }}
          />
          {form.category === "S" ? (
            <InputText
              label={say("VAT rate")}
              inputMode="decimal"
              className="num"
              value={form.rate ?? ""}
              onChange={(event) => setForm({ ...form, rate: event.target.value })}
            />
          ) : null}
        </div>
        <InputText
          label="Default amount · optional"
          inputMode="decimal"
          className="num"
          placeholder="Empty if it varies"
          value={form.defaultAmount}
          onChange={(event) => setForm({ ...form, defaultAmount: event.target.value })}
        />
      </div>
    </Dialog>
  );
}
